import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withTheme, taggedStylesToString } from 'common/styling/theme';
import { staticStyles, getDynamicStyles } from './style';

const ResponsiveTable = ({ data, columns, onRowClick, isNewTable, isLpaTable, theme }) => {
  const dynamicStyles = Object.keys(theme).length ? taggedStylesToString(getDynamicStyles(theme)) : ` `;

  // Filter out empty columns once at the component level
  const validColumns = useMemo(() => columns.filter(col => col && (col.key || col.Cell)), [columns]);

  const cellBuilder = rowItem =>
    isNewTable
      ? {
          row: {
            original: {
              ...rowItem,
            },
          },
        }
      : {
          original: {
            ...rowItem,
          },
        };

  return (
    <div className="ResponsiveTable">
      <div className="ResponsiveTable__thead">
        {validColumns.map((column, columnId) =>
          column.key ? (
            <FormattedMessage id={column.key} key={columnId} defaultMessage={column.key}>
              {txt => <span>{txt}</span>}
            </FormattedMessage>
          ) : null
        )}
        <span />
      </div>
      <div className="ResponsiveTable__tbody">
        {data.map((rowItem, index) => {
          const rowItemId = isLpaTable ? index : rowItem.id;

          return (
            <div className="ResponsiveTable__row" key={rowItemId}>
              <div
                className="ResponsiveTable__row-preview"
                onClick={() => {
                  const id = isLpaTable ? rowItem.user.id : rowItemId;
                  const title = rowItem?.title ?? '';
                  const user = rowItem?.user ?? {};

                  onRowClick(rowItemId, { original: { id, title, user, ...rowItem } });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const id = isLpaTable ? rowItem.user.id : rowItemId;
                    const title = rowItem?.title ?? '';
                    const user = rowItem?.user ?? {};
                    onRowClick(rowItemId, { original: { id, title, user, ...rowItem } });
                  }
                }}>
                {validColumns.map((column, columnId) => {
                  // Skip columns without key or Cell
                  if (!column.key && !column.Cell) return null;

                  return (
                    <div className="ResponsiveTable__row-item" key={`ResponsiveTable-${rowItemId}-${columnId}`}>
                      {column.Cell ? column.Cell(cellBuilder(rowItem)) : rowItem[column.accessor]}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {columns.some(column => column.Footer) && (
        <div className="ResponsiveTable__tfoot">
          <div className="ResponsiveTable__row-details">
            {columns
              .filter(column => column.Footer)
              .map((column, columnId) => (
                <div className="ResponsiveTable__details-item" key={`ResponsiveTable-footer-${columnId}`}>
                  {column.Footer}
                </div>
              ))}
          </div>
        </div>
      )}

      <style jsx global>
        {staticStyles}
      </style>
      <style>{dynamicStyles}</style>
    </div>
  );
};

ResponsiveTable.propTypes = {
  columns: PropTypes.array.isRequired,
  onRowClick: PropTypes.func.isRequired,
  data: PropTypes.array,
  isNewTable: PropTypes.bool,
  isLpaTable: PropTypes.bool,
  theme: PropTypes.object,
};

ResponsiveTable.defaultProps = {
  data: [],
  isNewTable: false,
  isLpaTable: false,
  theme: {},
};

export default withTheme()(ResponsiveTable);
export { ResponsiveTable };
